import React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string;
  label: React.ReactNode; // puede ser texto o nodo con estilos
  searchText?: string; // texto plano para búsqueda
  disabled?: boolean;
}

interface SelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  required?: boolean;
  searchable?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  className?: string;
  wrapperClassName?: string;
}

const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  required = false,
  searchable = false,
  searchQuery = '',
  onSearchChange,
  className = '',
  wrapperClassName = '',
}) => {
  // Filter out any options with empty values to prevent the error
  const validOptions = options.filter(option => option.value !== '');
  
  // Filter options based on search query if searchable is true
  const filteredOptions = searchable && searchQuery
    ? validOptions.filter(option => {
        const base = (option.searchText ?? (typeof option.label === 'string' ? option.label : ''))
          .toLowerCase();
        return base.includes(searchQuery.toLowerCase());
      })
    : validOptions;

  return (
    <div className={wrapperClassName || 'mb-4'}>
      {label ? (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      ) : null}
      <SelectPrimitive.Root 
        value={value} 
        onValueChange={onChange}
      >
        <SelectPrimitive.Trigger
          className={`w-full h-12 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm bg-white text-gray-900 ${className}`}
        >
          <div className="flex justify-between items-center">
            <SelectPrimitive.Value>
              {validOptions.find(opt => opt.value === value)?.label || 'Seleccionar...'}
            </SelectPrimitive.Value>
            <SelectPrimitive.Icon>
              <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </SelectPrimitive.Icon>
          </div>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className="bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-[9999]"
          >
            {searchable && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  placeholder="Buscar..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                />
              </div>
            )}
            <SelectPrimitive.Viewport className="p-1">
              {filteredOptions.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className="relative flex items-center px-8 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-900 dark:hover:text-indigo-100 cursor-pointer outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  onPointerUp={(e) => {
                    // Algunos navegadores iPadOS con Apple Pencil no disparan click.
                    if (e.pointerType === 'pen') {
                      onChange(option.value);
                      // Cerrar el dropdown manualmente para stylus
                      try {
                        (document.activeElement as HTMLElement)?.blur();
                      } catch {}
                    }
                  }}
                >
                  <SelectPrimitive.ItemText>
                    {option.label}
                  </SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
};

export default Select;